import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { ConfidenceChip, UnconfirmedChip, formatRelationship } from './procedural-badges';
import { formatDate, formatDateRange } from './procedural-helpers';
import { ProceduralSourceRef } from './procedural-source-drawer';
import type {
  ProceduralTimelineEventSuggestion,
  SuggestionKind,
} from '../../../../../../../../backend/src/matter/procedural-timeline/procedural-timeline-event-suggestion.entity';
import type { ProceduralTimelineEvent } from '../../../../../../../../backend/src/matter/procedural-timeline/procedural-timeline-event.entity';
import {
  useAcceptProceduralSuggestion,
  useRejectProceduralSuggestion,
} from '@/api/mutations/useProceduralSuggestionAction';
import { Chip } from '@/new/components/chip';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { formatTimeZoneLabel } from '@/new/lib/time-zones';
import { cn } from '@/new/lib/utils';

interface ProceduralSuggestionRowProps {
  suggestion: ProceduralTimelineEventSuggestion;
  eventsById: Record<string, ProceduralTimelineEvent>;
  onOpenSource: (source: ProceduralSourceRef) => void;
}

const KIND_LABELS: Record<SuggestionKind, string> = {
  add: 'New',
  update: 'Update',
  delete: 'Delete',
};

const KIND_CHIP_CLASSES: Record<SuggestionKind, string> = {
  add: 'border-green-200 bg-green-50 text-green-700',
  update: 'border-sky-200 bg-sky-50 text-sky-700',
  delete: 'border-red-200 bg-red-50 text-red-700',
};

const KIND_ICON_CLASSES: Record<SuggestionKind, string> = {
  add: 'lucide--plus-circle text-green-600',
  update: 'lucide--pencil text-sky-600',
  delete: 'lucide--minus-circle text-red-600',
};

interface EffectiveView {
  name: string;
  description: string | null;
  startDateTime: Date | string | null;
  endDateTime: Date | string | null;
  timeZone: string | null;
}

function effectiveView(suggestion: ProceduralTimelineEventSuggestion): EffectiveView {
  const existing = suggestion.existingEvent;
  if (suggestion.kind === 'delete') {
    return {
      name: existing?.name ?? '(unknown event)',
      description: existing?.description ?? null,
      startDateTime: existing?.startDateTime ?? null,
      endDateTime: existing?.endDateTime ?? null,
      timeZone: existing?.timeZone ?? null,
    };
  }
  return {
    name: suggestion.proposedName ?? existing?.name ?? '(unnamed)',
    description: suggestion.proposedDescription ?? existing?.description ?? null,
    startDateTime: suggestion.proposedStartDateTime ?? existing?.startDateTime ?? null,
    endDateTime: suggestion.proposedEndDateTime ?? existing?.endDateTime ?? null,
    timeZone: suggestion.proposedTimeZone ?? existing?.timeZone ?? null,
  };
}

interface ChangedFields {
  description: boolean;
  startDateTime: boolean;
  endDateTime: boolean;
  timeZone: boolean;
  /** Any of interval, interval type, or anchor target changed. */
  anchor: boolean;
}

const CHANGED_LABELS: Record<keyof ChangedFields, string> = {
  description: 'Description',
  startDateTime: 'Start date',
  endDateTime: 'End date',
  timeZone: 'Time zone',
  anchor: 'Linked date',
};

function detectChanges(s: ProceduralTimelineEventSuggestion): ChangedFields | null {
  // Backend contract: for kind=update, proposed* fields are non-null only when
  // they differ from the existing event — so a non-null proposed field is
  // exactly what changed.
  if (s.kind !== 'update') return null;
  return {
    description: s.proposedDescription !== null,
    startDateTime: s.proposedStartDateTime !== null,
    endDateTime: s.proposedEndDateTime !== null,
    timeZone: s.proposedTimeZone !== null,
    anchor:
      s.proposedAnchorExistingEventId !== null ||
      s.proposedDeltaInterval !== null ||
      s.proposedDeltaIntervalType !== null,
  };
}

export function ProceduralSuggestionRow({ suggestion, eventsById, onOpenSource }: ProceduralSuggestionRowProps) {
  const { matterId } = useParams();
  const { mutate: accept, isLoading: isAccepting } = useAcceptProceduralSuggestion();
  const { mutate: reject, isLoading: isRejecting } = useRejectProceduralSuggestion();

  const view = effectiveView(suggestion);
  const chunk = suggestion.chunk;
  const sourceFileName = chunk?.file?.name ?? null;
  const isDelete = suggestion.kind === 'delete';
  const isUpdate = suggestion.kind === 'update';
  const isPending = isAccepting || isRejecting;
  const isBlocked = suggestion.proposedAnchorSuggestionId !== null;
  const existing = suggestion.existingEvent;
  const changed = detectChanges(suggestion);
  const changedLabels = changed
    ? (Object.keys(changed) as Array<keyof ChangedFields>).filter((k) => changed[k]).map((k) => CHANGED_LABELS[k])
    : [];

  const anchorEventName =
    suggestion.proposedAnchorExistingEventId !== null
      ? (eventsById[suggestion.proposedAnchorExistingEventId]?.name ?? null)
      : null;
  const relationship = formatRelationship(
    suggestion.proposedDeltaInterval,
    suggestion.proposedDeltaIntervalType,
    anchorEventName,
  );
  const dateRangeText = formatDateRange(view.startDateTime, view.endDateTime);

  const oldAnchorEventName = existing?.anchorEventId ? (eventsById[existing.anchorEventId]?.name ?? null) : null;
  const oldRelationship = changed?.anchor
    ? formatRelationship(existing?.deltaInterval ?? null, existing?.deltaIntervalType ?? null, oldAnchorEventName)
    : null;
  const oldDateRangeText =
    changed?.startDateTime || changed?.endDateTime
      ? formatDateRange(existing?.startDateTime ?? null, existing?.endDateTime ?? null)
      : null;

  const handleSourceClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!chunk?.file) return;
    onOpenSource({
      fileId: chunk.file.id,
      fileName: chunk.file.name,
      pageNumber: chunk.pageNumber,
      chunkId: chunk.id,
      content: chunk.content,
      boundingBox: chunk.boundingBox,
    });
  };

  const handleAccept = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!matterId) return;
    accept(
      { matterId, suggestionId: suggestion.id },
      {
        onError: (error) => {
          toast.error(error.message || 'Failed to accept suggestion');
        },
      },
    );
  };

  const handleReject = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!matterId) return;
    reject(
      { matterId, suggestionId: suggestion.id },
      {
        onError: (error) => {
          toast.error(error.message || 'Failed to reject suggestion');
        },
      },
    );
  };

  return (
    <div
      className={cn(
        'w-full rounded-lg border border-dashed border-gray-300 bg-white px-4 py-3',
        isBlocked && 'opacity-60',
      )}
    >
      <div className="flex w-full flex-col gap-2 pr-2 text-left">
        <div className="flex items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <p
                  className={cn(
                    'flex min-w-0 items-center gap-2 truncate text-lg text-gray-900',
                    isDelete && 'text-gray-500 line-through',
                  )}
                >
                  {changed?.startDateTime && existing?.startDateTime && (
                    <span className="text-gray-400 line-through">{formatDate(existing.startDateTime)}</span>
                  )}
                  {view.startDateTime !== null && (
                    <>
                      <span className="text-gray-500">{formatDate(view.startDateTime)}</span>
                      <span className="text-sm text-gray-400/60">•</span>
                    </>
                  )}
                  <span className="truncate">{view.name}</span>
                </p>
              </TooltipTrigger>
              <TooltipContent>{view.name}</TooltipContent>
            </Tooltip>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {sourceFileName && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={handleSourceClick}
                    className="flex max-w-48 items-center gap-1.5 truncate text-xs text-blue-600 hover:underline"
                  >
                    <Icon className="lucide--file-text shrink-0" />
                    <span className="truncate">{sourceFileName}</span>
                  </button>
                </TooltipTrigger>
                <TooltipContent>{sourceFileName}</TooltipContent>
              </Tooltip>
            )}
            {isBlocked ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span tabIndex={0} className="inline-flex cursor-not-allowed items-center gap-2">
                    <Button variant="primary" size="sm" disabled>
                      <Icon className="lucide--check" />
                      Accept
                    </Button>
                    <Button variant="ghost" size="sm" disabled>
                      <Icon className="lucide--x" />
                      Reject
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>Verify the suggestion this event depends on first.</TooltipContent>
              </Tooltip>
            ) : (
              <>
                <Button variant="primary" size="sm" onClick={handleAccept} disabled={isPending}>
                  <Icon className={isAccepting ? 'lucide--loader-2 animate-spin' : 'lucide--check'} />
                  Accept
                </Button>
                <Button variant="ghost" size="sm" onClick={handleReject} disabled={isPending}>
                  <Icon className={isRejecting ? 'lucide--loader-2 animate-spin' : 'lucide--x'} />
                  Reject
                </Button>
              </>
            )}
          </div>
        </div>

        {isUpdate && changedLabels.length > 0 && (
          <div>
            <Chip className={cn('rounded-lg border', KIND_CHIP_CLASSES.update)}>
              <Icon className={KIND_ICON_CLASSES.update} />
              <span>
                <span className="font-semibold">Changed</span> {changedLabels.join(', ')}
              </span>
            </Chip>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-1.5">
          {!(isUpdate && changedLabels.length > 0) && (
            <Chip className={cn('rounded-lg border', KIND_CHIP_CLASSES[suggestion.kind])}>
              <Icon className={KIND_ICON_CLASSES[suggestion.kind]} />
              {KIND_LABELS[suggestion.kind]}
            </Chip>
          )}
          <UnconfirmedChip />
          <ConfidenceChip confidence={suggestion.proposedConfidence} />
          {oldDateRangeText && (
            <Chip className="rounded-lg border border-gray-200 bg-gray-50 text-gray-400 line-through">
              <Icon className="lucide--clock text-gray-300" />
              {oldDateRangeText}
            </Chip>
          )}
          {dateRangeText && (
            <Chip className="rounded-lg border border-gray-200 bg-gray-50 text-gray-700">
              <Icon className="lucide--clock text-gray-500" />
              {dateRangeText}
            </Chip>
          )}
          {changed?.timeZone && existing?.timeZone && (
            <Chip className="rounded-lg border border-gray-200 bg-gray-50 text-gray-400 line-through">
              <Icon className="lucide--globe text-gray-300" />
              {formatTimeZoneLabel(existing.timeZone)}
            </Chip>
          )}
          {view.timeZone && (
            <Chip className="rounded-lg border border-gray-200 bg-gray-50 text-gray-700">
              <Icon className="lucide--globe text-gray-500" />
              {formatTimeZoneLabel(view.timeZone)}
            </Chip>
          )}
          {oldRelationship && (
            <Chip className="rounded-lg border border-sky-200 bg-sky-50 text-sky-400 line-through">
              <Icon className="lucide--link text-sky-300" />
              <span className="block max-w-[20rem] truncate">{oldRelationship}</span>
            </Chip>
          )}
          {relationship && (
            <Chip className="rounded-lg border border-sky-200 bg-sky-50 text-sky-700">
              <Icon className="lucide--link text-sky-600" />
              <span className="block max-w-[20rem] truncate">{relationship}</span>
            </Chip>
          )}
        </div>

        {changed?.description && existing?.description && (
          <p className="text-sm text-gray-400 line-through">{existing.description}</p>
        )}
        {view.description && (
          <p className={cn('text-sm text-gray-600', isDelete && 'line-through')}>{view.description}</p>
        )}

        {suggestion.reasoning && (
          <p className="flex items-start gap-1.5 text-xs text-gray-500 italic">
            <Icon className="lucide--sparkles mt-0.5 shrink-0 text-gray-400" />
            <span>{suggestion.reasoning}</span>
          </p>
        )}
      </div>
    </div>
  );
}
